import React from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import * as turf from '@turf/turf';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import MenuItem from '@material-ui/core/MenuItem';
import Slider from '@material-ui/core/Slider';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';

import {
    convertMultiPolygonsToPolygons,
    getGeometryInfo,
    getInitialRangeSeparation,
    getValueColor,
    mergeAllPolygons,
    mergePolygonsByRange
} from './geometry';
import './GeoPreviewDialog.css';

const createPropertiesPopup = (properties, visibilityOptions) => {
    const container = document.createElement('div');
    const title = document.createElement('strong');
    const canEditName = visibilityOptions &&
        typeof visibilityOptions.onNameChange === 'function';
    const canEditAverageValue = visibilityOptions &&
        typeof visibilityOptions.onAverageValueChange === 'function';
    const keys = Object.keys(properties || {});

    if (canEditAverageValue && keys.indexOf('average_value') < 0) {
        keys.unshift('average_value');
    }
    if (canEditName && keys.indexOf('name') < 0) {
        keys.unshift('name');
    }

    title.textContent = 'Atributos';
    container.appendChild(title);

    if (visibilityOptions) {
        const visibilityLabel = document.createElement('label');
        const visibilityInput = document.createElement('input');
        const visibilityText = document.createElement('span');

        visibilityLabel.style.display = 'flex';
        visibilityLabel.style.alignItems = 'center';
        visibilityLabel.style.gap = '0.4rem';
        visibilityLabel.style.marginTop = '0.5rem';
        visibilityLabel.style.cursor = 'pointer';

        visibilityInput.type = 'checkbox';
        visibilityInput.checked = visibilityOptions.visible;
        visibilityInput.addEventListener('change', event => {
            visibilityOptions.onChange(event.target.checked);
        });

        visibilityText.textContent = 'Exibir no mapa';
        visibilityLabel.appendChild(visibilityInput);
        visibilityLabel.appendChild(visibilityText);
        container.appendChild(visibilityLabel);
    }

    if (keys.length === 0) {
        const empty = document.createElement('div');
        empty.textContent = 'Sem atributos';
        container.appendChild(empty);
        return container;
    }

    const table = document.createElement('table');
    table.style.marginTop = '0.5rem';
    table.style.borderCollapse = 'collapse';

    keys.forEach(key => {
        if(key === 'poly_type') {
            return;
        }
        const row = document.createElement('tr');
        const name = document.createElement('th');
        const value = document.createElement('td');
        const propertyValue = (properties || {})[key];

        name.textContent = key;
        name.style.padding = '0.2rem 0.5rem 0.2rem 0';
        name.style.textAlign = 'left';
        name.style.verticalAlign = 'top';

        if (key === 'name' && canEditName) {
            const input = document.createElement('input');
            name.textContent = 'Nome';
            input.type = 'text';
            input.value = propertyValue === null || typeof propertyValue === 'undefined'
                ? ''
                : String(propertyValue);
            input.style.width = '12rem';
            input.style.padding = '0.25rem';
            input.addEventListener('input', event => {
                visibilityOptions.onNameChange(event.target.value);
            });
            value.appendChild(input);
        } else if (key === 'average_value' && canEditAverageValue) {
            const input = document.createElement('input');
            name.textContent = 'Vazão';
            input.type = 'number';
            input.step = 'any';
            input.value = propertyValue === null || typeof propertyValue === 'undefined'
                ? ''
                : String(propertyValue);
            input.style.width = '7rem';
            input.style.padding = '0.25rem';
            input.addEventListener('input', event => {
                visibilityOptions.onAverageValueChange(event.target.value);
            });
            value.appendChild(input);
        } else {
            value.textContent = propertyValue !== null && typeof propertyValue === 'object'
                ? JSON.stringify(propertyValue)
                : String(propertyValue === null || typeof propertyValue === 'undefined' ? '' : propertyValue);
        }
        value.style.padding = '0.2rem 0';
        value.style.maxWidth = '20rem';
        value.style.wordBreak = 'break-word';

        row.appendChild(name);
        row.appendChild(value);
        table.appendChild(row);
    });

    container.appendChild(table);
    return container;
};

const getFeatureName = feature => {
    const properties = feature && feature.properties ? feature.properties : {};
    const name = properties.name;

    return name === null || typeof name === 'undefined' || String(name).trim() === ''
        ? 'area'
        : String(name);
};

class GeoPreviewDialog extends React.Component {
    state = {
        panel: 'list',
        rangeProperty: '',
        rangeMin: '',
        rangeMax: '',
        rangeSeparation: 0,
        displayFeatures: null,
        featureVisibility: [],
        backgroundVisible: true
    };

    mapContainer = React.createRef();

    map = null;

    geoJsonLayer = null;

    backgroundLayer = null;

    labelLayer = null;

    featureLayers = [];

    labelMarkers = [];

    backgroundSource = null;

    backgroundFeatures = [];

    renderTimer = null;

    preserveVisibilityOnNextRender = false;

    componentDidUpdate(previousProps, previousState) {
        if (!previousProps.open && this.props.open) {
            if (this.props.preserveState) {
                this.preserveVisibilityOnNextRender = true;
            } else {
                this.resetMatrixControls();
            }
        } else if (
            this.props.open &&
            previousProps.features !== this.props.features
        ) {
            this.resetMatrixControls(true);
        }

        if (previousProps.open && !this.props.open) {
            this.destroyMap();
        } else if (
            this.props.open &&
            this.map &&
            (
                previousProps.features !== this.props.features ||
                previousState.rangeProperty !== this.state.rangeProperty
            )
        ) {
            this.renderFeatures();
        } else if (
            this.props.open &&
            this.map &&
            previousState.rangeSeparation !== this.state.rangeSeparation
        ) {
            this.scheduleRenderFeatures();
        }
    }

    resetMatrixControls = renderMap => {
        const polygonFeatures = convertMultiPolygonsToPolygons(this.props.features);
        const geometryInfo = getGeometryInfo(polygonFeatures);
        const rangeProperty = geometryInfo.numericProperties[0];
        const initialSeparation = rangeProperty
            ? getInitialRangeSeparation(rangeProperty.min, rangeProperty.max)
            : 0;

        this.setState({
            panel: 'list',
            rangeProperty: rangeProperty ? rangeProperty.name : '',
            rangeMin: rangeProperty ? Math.round(rangeProperty.min) : '',
            rangeMax: rangeProperty ? Math.round(rangeProperty.max) : '',
            rangeSeparation: initialSeparation,
            displayFeatures: polygonFeatures,
            featureVisibility: polygonFeatures.map(() => true),
            backgroundVisible: true
        }, () => {
            if (renderMap && this.map) this.renderFeatures();
        });
    };

    updateRangeFromFeatures = () => {
        const geometryInfo = getGeometryInfo(this.props.features);
        const selectedProperty = geometryInfo.numericProperties.find(
            property => property.name === this.state.rangeProperty
        ) || geometryInfo.numericProperties[0];

        this.setState({
            rangeProperty: selectedProperty ? selectedProperty.name : '',
            rangeMin: selectedProperty ? Math.round(selectedProperty.min) : '',
            rangeMax: selectedProperty ? Math.round(selectedProperty.max) : '',
            rangeSeparation: selectedProperty
                ? Math.min(
                    Math.max(
                        0,
                        Number.isFinite(Number(this.state.rangeSeparation))
                            ? Number(this.state.rangeSeparation)
                            : 0
                    ),
                    this.getRangeDifference(selectedProperty)
                )
                : 0
        });
    };

    getRangeDifference = rangeProperty => {
        if (!rangeProperty) {
            return 0;
        }

        return Math.max(
            0,
            Math.round(Number(rangeProperty.max) - Number(rangeProperty.min))
        );
    };

    changeRangeProperty = event => {
        const geometryInfo = getGeometryInfo(this.props.features);
        const selectedProperty = geometryInfo.numericProperties.find(
            property => property.name === event.target.value
        );

        this.setState({
            rangeProperty: selectedProperty ? selectedProperty.name : '',
            rangeMin: selectedProperty ? Math.round(selectedProperty.min) : '',
            rangeMax: selectedProperty ? Math.round(selectedProperty.max) : '',
            rangeSeparation: selectedProperty
                ? getInitialRangeSeparation(selectedProperty.min, selectedProperty.max)
                : 0
        });
    };

    changeRangeSeparation = value => {
        const geometryInfo = getGeometryInfo(this.props.features);
        const selectedProperty = geometryInfo.numericProperties.find(
            property => property.name === this.state.rangeProperty
        );
        const max = this.getRangeDifference(selectedProperty);
        const numericValue = Number(value);

        this.setState({
            rangeSeparation: Math.min(
                max,
                Math.max(0, Number.isFinite(numericValue) ? numericValue : 0)
            )
        });
    };

    componentWillUnmount() {
        this.destroyMap();
    }

    scheduleRenderFeatures = () => {
        if (this.renderTimer) {
            clearTimeout(this.renderTimer);
        }

        this.renderTimer = setTimeout(() => {
            this.renderTimer = null;
            this.renderFeatures();
        }, 150);
    };

    initializeMap = () => {
        if (!this.mapContainer.current) {
            return;
        }

        if (!this.map) {
            this.map = L.map(this.mapContainer.current, {
                zoomControl: true,
                attributionControl: false
            });

            L.tileLayer(this.props.tileUrl, this.props.tileOptions).addTo(this.map);
        }

        this.map.invalidateSize();
        this.renderFeatures();
    };

    destroyMap = () => {
        if (this.renderTimer) {
            clearTimeout(this.renderTimer);
            this.renderTimer = null;
        }

        if (this.map) {
            this.map.remove();
            this.map = null;
            this.geoJsonLayer = null;
            this.backgroundLayer = null;
            this.labelLayer = null;
            this.featureLayers = [];
            this.labelMarkers = [];
            this.backgroundSource = null;
            this.backgroundFeatures = [];
        }
    };

    getBackgroundFeatures = () => {
        if (this.backgroundSource !== this.props.features) {
            this.backgroundSource = this.props.features;
            this.backgroundFeatures = mergeAllPolygons(this.props.features);
        }

        return this.backgroundFeatures;
    };

    setBackgroundVisibility = visible => {
        this.setState({ backgroundVisible: visible }, () => {
            if (!this.map || !this.backgroundLayer) {
                return;
            }

            if (visible) {
                this.backgroundLayer.addTo(this.map);
                this.backgroundLayer.bringToBack();
            } else {
                this.backgroundLayer.remove();
            }
        });
    };

    setFeatureVisibility = (index, visible) => {
        this.setState(previousState => {
            const featureVisibility = previousState.featureVisibility.slice();
            featureVisibility[index] = visible;

            return { featureVisibility };
        }, () => this.updateFeatureVisibility(index));
    };

    setFeatureAverageValue = (index, value) => {
        const normalizedValue = String(value).trim().replace(',', '.');
        const averageValue = normalizedValue === '' ? null : Number(normalizedValue);

        if (normalizedValue !== '' && !Number.isFinite(averageValue)) return;

        this.setFeatureProperty(index, 'average_value', averageValue);
    };

    setFeatureName = (index, value) => {
        this.setFeatureProperty(index, 'name', value);
    };

    setFeatureProperty = (index, propertyName, value) => {
        this.setState(previousState => {
            const displayFeatures = (previousState.displayFeatures || []).slice();
            const feature = displayFeatures[index];
            if (!feature) return null;

            displayFeatures[index] = {
                ...feature,
                properties: {
                    ...(feature.properties || {}),
                    [propertyName]: value
                }
            };

            return { displayFeatures };
        }, () => {
            const layer = this.featureLayers[index];
            const feature = this.state.displayFeatures[index];
            if (layer && feature) layer.feature = feature;

            if (propertyName === 'average_value') {
                const geometryInfo = getGeometryInfo(this.props.features);
                const rangeProperty = geometryInfo.numericProperties.find(
                    property => property.name === this.state.rangeProperty
                ) || geometryInfo.numericProperties[0];

                this.renderValueLabels(
                    this.state.displayFeatures,
                    rangeProperty,
                    this.state.featureVisibility
                );
            }
        });
    };

    getFeaturePopupOptions = (index, visible) => ({
        visible,
        onChange: checked => this.setFeatureVisibility(index, checked),
        onNameChange: value => this.setFeatureName(index, value),
        onAverageValueChange: value => this.setFeatureAverageValue(index, value)
    });

    updateFeatureVisibility = index => {
        if (!this.geoJsonLayer) {
            return;
        }

        const visible = this.state.featureVisibility[index] !== false;
        const layer = this.featureLayers[index];
        const markers = this.labelMarkers[index] || [];
        const feature = this.state.displayFeatures[index];

        if (layer) {
            if (visible && !this.geoJsonLayer.hasLayer(layer)) {
                this.geoJsonLayer.addLayer(layer);
            } else if (!visible && this.geoJsonLayer.hasLayer(layer)) {
                this.geoJsonLayer.removeLayer(layer);
            }

            layer.bindPopup(createPropertiesPopup(
                feature.properties,
                this.getFeaturePopupOptions(index, visible)
            ));
        }

        markers.forEach(marker => {
            if (visible && !this.labelLayer.hasLayer(marker)) {
                this.labelLayer.addLayer(marker);
            } else if (!visible && this.labelLayer.hasLayer(marker)) {
                this.labelLayer.removeLayer(marker);
            }
        });
    };

    getFeaturesForConfirm = () => {
        const hasPendingRangeUpdate = Boolean(this.renderTimer);
        const features = hasPendingRangeUpdate
            ? this.getMapFeatureData().mapFeatures
            : (this.state.displayFeatures || []);
        const visibleFeatures = features.filter((feature, index) =>
            hasPendingRangeUpdate || this.state.featureVisibility[index] !== false
        );

        return {
            features: this.getBackgroundFeatures().concat(visibleFeatures),
            resumeFeatures: visibleFeatures
        };
    };

    confirmFeatures = () => {
        const confirmData = this.getFeaturesForConfirm();
        this.props.onConfirm(confirmData.features, confirmData.resumeFeatures);
    };

    renderValueLabels = (features, rangeProperty, visibility) => {
        if (this.labelLayer) {
            this.labelLayer.remove();
        }

        this.labelLayer = L.layerGroup().addTo(this.map);
        this.labelMarkers = features.map(() => []);

        features.forEach((feature, featureIndex) => {
            const geometry = feature && feature.geometry;
            const properties = feature && feature.properties ? feature.properties : {};
            const hasAverageValue = properties.average_value !== null &&
                typeof properties.average_value !== 'undefined' &&
                String(properties.average_value).trim() !== '';
            const rangePropertyValue = rangeProperty ? properties[rangeProperty.name] : null;
            const hasRangeValue = rangePropertyValue !== null &&
                typeof rangePropertyValue !== 'undefined' &&
                String(rangePropertyValue).trim() !== '';
            const averageValue = hasAverageValue ? Number(properties.average_value) : NaN;
            const rangeValue = hasRangeValue ? Number(rangePropertyValue) : NaN;
            const labelValue = Number.isFinite(averageValue) ? averageValue : rangeValue;

            if (
                !geometry ||
                (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') ||
                !Number.isFinite(labelValue)
            ) {
                return;
            }
            const label = String(Math.round(labelValue));
            const polygons = geometry.type === 'Polygon'
                ? [geometry.coordinates]
                : geometry.coordinates;

            polygons.forEach(coordinates => {
                try {
                    const center = turf.pointOnFeature(
                        turf.polygon(coordinates)
                    ).geometry.coordinates;
                    const icon = L.divIcon({
                        className: 'geo-range-label-icon',
                        html: `<span class="geo-range-label-number">${label}</span>`,
                        iconSize: [0, 0]
                    });

                    const marker = L.marker([center[1], center[0]], {
                        icon,
                        interactive: false,
                        keyboard: false
                    });

                    this.labelMarkers[featureIndex].push(marker);

                    if (visibility[featureIndex] !== false) {
                        marker.addTo(this.labelLayer);
                    }
                } catch (error) {
                    // Uma geometria inválida continua visível, apenas sem o label.
                }
            });
        });
    };

    getMapFeatureData = () => {
        const sourceFeatures = convertMultiPolygonsToPolygons(this.props.features)
            .filter(feature => feature && feature.geometry);
        const geometryInfo = getGeometryInfo(sourceFeatures);
        const rangeProperty = geometryInfo.numericProperties.find(
            property => property.name === this.state.rangeProperty
        ) || geometryInfo.numericProperties[0];
        const mapFeatures = rangeProperty
            ? mergePolygonsByRange(
                sourceFeatures,
                rangeProperty.name,
                this.state.rangeSeparation
            )
            : sourceFeatures.map(feature => ({
                ...feature,
                properties: {
                    ...feature.properties,
                    poly_type: 'F'
                }
            }));

        return {
            rangeProperty,
            mapFeatures
        };
    };

    renderFeatures = () => {
        if (!this.map) {
            return;
        }

        if (this.geoJsonLayer) {
            this.geoJsonLayer.remove();
        }

        if (this.backgroundLayer) {
            this.backgroundLayer.remove();
            this.backgroundLayer = null;
        }

        if (this.labelLayer) {
            this.labelLayer.remove();
            this.labelLayer = null;
        }

        const mapFeatureData = this.getMapFeatureData();
        const rangeProperty = mapFeatureData.rangeProperty;
        const canPreserveFeatures = this.preserveVisibilityOnNextRender &&
            Array.isArray(this.state.displayFeatures) &&
            this.state.displayFeatures.length > 0;
        const preservedFeatures = convertMultiPolygonsToPolygons(this.props.features)
            .filter(feature => feature && feature.geometry);
        const mapFeatures = canPreserveFeatures
            ? preservedFeatures
            : mapFeatureData.mapFeatures;
        const featureCollection = {
            type: 'FeatureCollection',
            features: mapFeatures
        };
        const canPreserveVisibility = this.preserveVisibilityOnNextRender &&
            this.state.featureVisibility.length === mapFeatures.length;
        const featureVisibility = canPreserveVisibility
            ? this.state.featureVisibility
            : mapFeatures.map(() => true);

        this.preserveVisibilityOnNextRender = false;

        this.mapFeatures = mapFeatures;
        this.featureLayers = [];
        this.setState({
            displayFeatures: mapFeatures,
            featureVisibility
        });

        this.backgroundLayer = L.geoJSON({
            type: 'FeatureCollection',
            features: this.getBackgroundFeatures()
        }, {
            smoothFactor: 0,
            style: {
                color: '#ffffff',
                weight: 5,
                opacity: 1,
                fill: false,
                fillOpacity: 0
            },
            onEachFeature: (feature, layer) => {
                layer.bindPopup(createPropertiesPopup(feature.properties, {
                    visible: this.state.backgroundVisible,
                    onChange: checked => this.setBackgroundVisibility(checked)
                }));
            }
        });

        if (this.state.backgroundVisible) {
            this.backgroundLayer.addTo(this.map);
        }

        let featureIndex = 0;
        this.geoJsonLayer = L.geoJSON(featureCollection, {
            smoothFactor: 0,
            style: feature => {
                const value = rangeProperty && feature.properties
                    ? feature.properties[rangeProperty.name]
                    : null;
                const color = rangeProperty
                    ? getValueColor(value, rangeProperty.min, rangeProperty.max)
                    : '#2f80ed';

                return {
                    color,
                    fillColor: color,
                    weight: 2,
                    opacity: 1,
                    fillOpacity: 0.65
                };
            },
            pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
                radius: 6,
                color: '#2f80ed',
                fillColor: '#ffffff',
                fillOpacity: 1,
                weight: 3,
            }),
            onEachFeature: (feature, layer) => {
                const index = featureIndex++;
                this.featureLayers[index] = layer;
                layer.bindPopup(createPropertiesPopup(
                    feature.properties,
                    this.getFeaturePopupOptions(index, featureVisibility[index])
                ));
            }
        }).addTo(this.map);

        if (this.state.backgroundVisible) {
            this.backgroundLayer.bringToBack();
        }
        this.renderValueLabels(mapFeatures, rangeProperty, featureVisibility);

        const bounds = this.geoJsonLayer.getBounds();

        if (bounds.isValid()) {
            this.map.fitBounds(bounds, {
                padding: [30, 30],
                maxZoom: 18
            });
        } else {
            this.map.setView([-15.7801, -47.9292], 4);
        }
    };

    render() {
        const {
            open,
            features,
            onCancel,
            title,
            cancelLabel,
            confirmLabel,
            onReplaceFile,
            replaceFileLabel
        } = this.props;
        const displayFeatures = Array.isArray(this.state.displayFeatures)
            ? this.state.displayFeatures
            : features;
        const geometryInfo = getGeometryInfo(features);
        const showControls = geometryInfo.canMerge && this.state.panel === 'control';
        const selectedRangeProperty = geometryInfo.numericProperties.find(
            property => property.name === this.state.rangeProperty
        ) || geometryInfo.numericProperties[0];
        const rangeDifference = this.getRangeDifference(selectedRangeProperty);
        const sliderMax = rangeDifference > 0 ? rangeDifference : 1;
        const sliderStep = rangeDifference > 0 && rangeDifference < 1
            ? rangeDifference
            : 1;
        const backgroundCount = (this.backgroundFeatures || []).length;

        return (
            <Dialog
                open={open}
                onClose={onCancel}
                fullWidth
                maxWidth="lg"
                PaperProps={{ style: { background: '#ffffff' } }}
                TransitionProps={{ onEntered: this.initializeMap }}
            >
                <DialogContent
                    style={{
                        padding: '1.5rem',
                        background: '#ffffff',
                        overflow: 'hidden'
                    }}
                >
                    <div
                        className="containerTop"
                        style={{
                            alignItems: 'center',
                            flexShrink: 0,
                            paddingBottom: '1rem'
                        }}
                    >
                        <div>
                            <h1 style={{ margin: 0 }}>
                                {title} ({displayFeatures.length})
                            </h1>
                        </div>
                        <div className="centerTop"></div>
                        <div>
                            {onReplaceFile && (
                                <React.Fragment>
                                    <Button variant="contained" component="label" className="popUpButton">
                                        {replaceFileLabel}
                                        <input
                                            hidden
                                            type="file"
                                            accept=".kml,.kmz,.shp,.zip"
                                            onChange={event => {
                                                onReplaceFile(event);
                                                event.target.value = '';
                                            }}
                                        />
                                    </Button>
                                    &nbsp;
                                </React.Fragment>
                            )}
                            <Button
                                variant="contained"
                                onClick={onCancel}
                                className="popUpButton"
                            >
                                {cancelLabel}
                            </Button>
                            &nbsp;
                            <Button
                                color="primary"
                                variant="contained"
                                className="popUpButton"
                                onClick={this.confirmFeatures}
                                disabled={displayFeatures.length === 0}
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '1rem',
                            height: '65vh',
                            minHeight: '25rem'
                        }}
                    >
                    <div
                        style={{
                            width: '18rem',
                            minWidth: '15rem',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid #dddddd',
                            borderRadius: '4px',
                            background: '#ffffff'
                        }}
                    >
                        {geometryInfo.canMerge && (
                            <Tabs
                                value={this.state.panel}
                                onChange={(event, panel) => this.setState({ panel })}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="fullWidth"
                            >
                                <Tab value="list" label="Lista" />
                                <Tab value="control" label="Controle" />
                            </Tabs>
                        )}

                        {showControls ? (
                            <div style={{ padding: '1rem', overflowY: 'auto' }}>
                                <strong>Range:</strong>

                                {geometryInfo.numericProperties.length > 1 && (
                                    <TextField
                                        select
                                        fullWidth
                                        margin="normal"
                                        label="Propriedade"
                                        value={this.state.rangeProperty}
                                        onChange={this.changeRangeProperty}
                                    >
                                        {geometryInfo.numericProperties.map(property => (
                                            <MenuItem key={property.name} value={property.name}>
                                                {property.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}

                                {geometryInfo.numericProperties.length === 1 && (
                                    <div
                                        style={{
                                            marginTop: '0.75rem',
                                            color: '#666666',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {geometryInfo.numericProperties[0].name}
                                    </div>
                                )}

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '0.75rem',
                                        marginTop: '0.5rem'
                                    }}
                                >
                                    <TextField
                                        type="number"
                                        label="Mínimo"
                                        value={this.state.rangeMin}
                                        inputProps={{ step: 'any' }}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                    />
                                    <TextField
                                        type="number"
                                        label="Máximo"
                                        value={this.state.rangeMax}
                                        inputProps={{ step: 'any' }}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                    />
                                </div>

                                <div style={{ marginTop: '1.5rem' }}>
                                    <strong>Separação:</strong>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            marginTop: '0.5rem'
                                        }}
                                    >
                                        <Slider
                                            value={this.state.rangeSeparation}
                                            min={0}
                                            max={sliderMax}
                                            step={sliderStep}
                                            onChange={(event, value) => this.changeRangeSeparation(value)}
                                            valueLabelDisplay="auto"
                                            aria-label="Separação do range"
                                            disabled={rangeDifference === 0}
                                            style={{ flex: 1 }}
                                        />

                                        <TextField
                                            type="number"
                                            value={this.state.rangeSeparation}
                                            onChange={event => this.changeRangeSeparation(event.target.value)}
                                            inputProps={{
                                                min: 0,
                                                max: rangeDifference,
                                                step: sliderStep
                                            }}
                                            disabled={rangeDifference === 0}
                                            style={{ width: '6rem' }}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            color: '#666666',
                                            fontSize: '0.75rem',
                                            marginTop: '0.25rem'
                                        }}
                                    >
                                        Limite: 0 até {rangeDifference}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {backgroundCount > 0 && (
                                    <div
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0.75rem',
                                            marginBottom: '0.75rem',
                                            background: '#ffffff',
                                            borderBottom: '2px solid #dddddd',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
                                            fontWeight: 600
                                        }}
                                    >
                                        <Checkbox
                                            checked={this.state.backgroundVisible}
                                            onChange={event =>
                                                this.setBackgroundVisibility(event.target.checked)
                                            }
                                            color="primary"
                                            size="small"
                                            style={{ padding: '0 0.5rem 0 0' }}
                                        />
                                        <span
                                            style={{
                                                color: this.state.backgroundVisible
                                                    ? 'inherit'
                                                    : '#aaaaaa'
                                            }}
                                        >
                                            Polígono base
                                        </span>
                                    </div>
                                )}

                                {displayFeatures.length === 0 && (
                                    <div style={{ padding: '1rem', color: '#666666' }}>
                                        Nenhuma geometria disponível.
                                    </div>
                                )}

                                {displayFeatures.map((feature, index) => (
                                    <div
                                        key={`${feature.id || 'feature'}-${index}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0.5rem 0.75rem',
                                            borderBottom: '1px solid #eeeeee'
                                        }}
                                    >
                                        <Checkbox
                                            checked={this.state.featureVisibility[index] !== false}
                                            onChange={event =>
                                                this.setFeatureVisibility(index, event.target.checked)
                                            }
                                            color="primary"
                                            size="small"
                                            style={{ padding: '0 0.5rem 0 0' }}
                                        />
                                        <span
                                            title={getFeatureName(feature)}
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: this.state.featureVisibility[index] !== false
                                                    ? 'inherit'
                                                    : '#aaaaaa'
                                            }}
                                        >
                                            {getFeatureName(feature)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div
                        ref={this.mapContainer}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            height: '100%',
                            background: '#eeeeee'
                        }}
                    />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
}

GeoPreviewDialog.propTypes = {
    open: PropTypes.bool,
    features: PropTypes.arrayOf(PropTypes.object),
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onReplaceFile: PropTypes.func,
    title: PropTypes.string,
    cancelLabel: PropTypes.string,
    confirmLabel: PropTypes.string,
    tileUrl: PropTypes.string,
    tileOptions: PropTypes.object,
    preserveState: PropTypes.bool,
    replaceFileLabel: PropTypes.string
};

GeoPreviewDialog.defaultProps = {
    open: false,
    features: [],
    title: 'Pré-visualização das geometrias',
    cancelLabel: 'Cancelar',
    confirmLabel: 'Continuar',
    replaceFileLabel: 'Selecionar outro arquivo',
    preserveState: false,
    tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    tileOptions: {
        attribution: '&copy; OpenStreetMap contributors'
    }
};

export default GeoPreviewDialog;
